import AdminLayout from "../../commponents/layout/adminLayout";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { TableColumn } from "../../commponents/table/types";
import { Eye, Pencil, Trash2 } from "lucide-react";

import {
  createCustomer,
  deleteCustomerById,
  editCustomer,
  getCustomer,
  getCustomerById,
} from "../../api/customerApi";
import Modal from "../../commponents/Modal";
import DataTable from "../../commponents/table/DataTable";
import { showConfirm, showError, showSuccess } from "../../utils/ alert";

interface Customer {
  id: number;
  customerName: string;
  companyName: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  npwp: string | null;
  status: string;
}

interface CustomerForm {
  customer_name: string;
  company_name: string;
  address: string;
  phone: string;
  email: string;
  npwp: string;
  status: string;
}

const STATUS_OPTIONS = ["Active", "Inactive"];

const initialForm: CustomerForm = {
  customer_name: "",
  company_name: "",
  address: "",
  phone: "",
  email: "",
  npwp: "",
  status: "Active",
};

export default function CustomerList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const [isEdit, setIsEdit] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form, setForm] = useState<CustomerForm>(initialForm);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const columns: TableColumn<Customer>[] = [
    {
      key: "customerName",
      title: "Customer Name",
    },
    {
      key: "companyName",
      title: "Company",
      render: (row) => <span>{row.companyName || "-"}</span>,
    },
    {
      key: "phone",
      title: "No. Telp",
      render: (row) => <span>{row.phone || "-"}</span>,
    },
    {
      key: "email",
      title: "Email",
      render: (row) => <span>{row.email || "-"}</span>,
    },
    {
      key: "status",
      title: "Status",
      render: (row) => (
        <span
          className={`rounded-full px-3 py-1 text-xs font-medium ${
            row.status === "Active"
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}>
          {row.status}
        </span>
      ),
    },
    {
      key: "id",
      title: "Action",
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/Customer/Detail/${row.id}`)}
            className="rounded-lg bg-blue-100 p-2 text-blue-600 hover:bg-blue-200">
            <Eye size={18} />
          </button>

          <button
            onClick={() => handleEdit(row.id)}
            className="rounded-lg bg-yellow-100 p-2 text-yellow-600 hover:bg-yellow-200">
            <Pencil size={18} />
          </button>

          <button
            onClick={() => handleDelete(row.id)}
            className="rounded-lg bg-red-100 p-2 text-red-600 hover:bg-red-200">
            <Trash2 size={18} />
          </button>
        </div>
      ),
    },
  ];

  const fetchCustomers = async () => {
    try {
      setLoading(true);

      const response = await getCustomer({
        page: currentPage,
        limit: pageSize,
      });

      setCustomers(response.data.customerList);
      setTotalPages(response.data.pagination.totalPages);
      setTotalData(response.data.pagination.totalData);
    } catch (error) {
      showError("Failed", "Failed to load customer data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  // Kalau datang dari halaman detail dengan ?editId=... langsung buka
  // modal edit untuk customer itu.
  useEffect(() => {
    const editId = searchParams.get("editId");

    if (editId) {
      handleEdit(Number(editId));
      searchParams.delete("editId");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    try {
      if (!form.customer_name.trim()) {
        showError("Failed", "Customer name is required.");
        return;
      }

      const payload = {
        customer_name: form.customer_name,
        company_name: form.company_name,
        address: form.address,
        phone: form.phone,
        email: form.email,
        npwp: form.npwp,
        status: form.status,
      };

      if (isEdit && selectedId !== null) {
        await editCustomer(selectedId, payload);
        await showSuccess("Success", "Customer updated successfully.");
      } else {
        await createCustomer(payload);
        await showSuccess("Success", "Customer created successfully.");
      }

      await fetchCustomers();

      handleCloseModal();
    } catch (error: any) {
      showError(
        "Failed",
        error?.response?.data?.message ?? "Failed to save customer."
      );
    }
  };

  const handleEdit = async (id: number) => {
    try {
      const { data } = await getCustomerById(id);
      const customer = data.data;

      setForm({
        customer_name: customer.customer_name ?? "",
        company_name: customer.company_name ?? "",
        address: customer.address ?? "",
        phone: customer.phone ?? "",
        email: customer.email ?? "",
        npwp: customer.npwp ?? "",
        status: customer.status ?? "Active",
      });

      setSelectedId(id);
      setIsEdit(true);
      setOpenModal(true);
    } catch (error) {
      showError("Failed", "Failed to load customer data.");
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const confirmed = await showConfirm(
        "Are you sure you want to delete this customer?"
      );

      if (!confirmed) return;

      await deleteCustomerById(id);

      await fetchCustomers();

      await showSuccess("Success", "Customer deleted successfully.");
    } catch (error: any) {
      showError(
        "Failed",
        error?.response?.data?.message ?? "Failed to delete customer."
      );
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setForm(initialForm);
    setSelectedId(null);
    setIsEdit(false);
  };

  const handleAdd = () => {
    setIsEdit(false);
    setSelectedId(null);
    setForm(initialForm);
    setOpenModal(true);
  };

  return (
    <AdminLayout>
      <div className="bg-white rounded-3xl p-6">
        <DataTable
          title="Customer"
          columns={columns}
          data={customers}
          onAdd={handleAdd}
          showSearch={false}
          showFilter={false}
          pagination={{
            currentPage,
            totalPages,
            pageSize,
            onPageChange: setCurrentPage,
            onPageSizeChange: (size) => {
              setPageSize(size);
              setCurrentPage(1);
            },
          }}
        />

        <Modal
          open={openModal}
          title={isEdit ? "Edit Customer" : "Add Customer"}
          onClose={handleCloseModal}>
          <div className="space-y-4">
            {/* Customer Name */}
            <div>
              <label className="mb-2 block font-semibold">
                Customer Name
              </label>
              <input
                type="text"
                name="customer_name"
                value={form.customer_name}
                onChange={handleChange}
                placeholder="Enter customer name"
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Company Name */}
            <div>
              <label className="mb-2 block font-semibold">
                Company Name
              </label>
              <input
                type="text"
                name="company_name"
                value={form.company_name}
                onChange={handleChange}
                placeholder="Enter company name"
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="mb-2 block font-semibold">No. Telp</label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="Enter phone number"
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Email */}
            <div>
              <label className="mb-2 block font-semibold">Email</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Enter email"
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* NPWP */}
            <div>
              <label className="mb-2 block font-semibold">NPWP</label>
              <input
                type="text"
                name="npwp"
                value={form.npwp}
                onChange={handleChange}
                placeholder="Enter NPWP"
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Address */}
            <div>
              <label className="mb-2 block font-semibold">Address</label>
              <textarea
                name="address"
                value={form.address}
                onChange={handleChange}
                placeholder="Enter address"
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Status */}
            <div>
              <label className="mb-2 block font-semibold">Status</label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none">
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            {/* Action */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={handleCloseModal}
                className="rounded-lg bg-gray-500 px-5 py-2 text-white hover:bg-gray-600">
                Cancel
              </button>

              <button
                onClick={handleSave}
                className="rounded-lg bg-blue-600 px-5 py-2 text-white hover:bg-blue-700">
                {isEdit ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
}
